import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { ExportDialog } from "@/components/ExportDialog";
import { FileText, Download, Plus, Search } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBulk, BulkCheck, BulkCheckAll, BulkBar } from "@/components/BulkDelete";
import { useAutoRefresh } from "@/lib/autoRefresh";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function CashRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("all");
  const bulk = useBulk();

  const canFilterBranch = ["direktur", "tim_keuangan", "it_admin"].includes(user?.role);

  const load = () => {
    const params = {};
    if (tab !== "all") params.status = tab;
    if (canFilterBranch && branch !== "all") params.branch = branch;
    api.get("/cash-requests", { params }).then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(load, [tab, branch]);
  useAutoRefresh(load);
  useEffect(() => {
    if (canFilterBranch) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, [canFilterBranch]);

  const canExport = ["it_admin", "direktur", "tim_keuangan"].includes(user?.role);
  const canRequestCash = ["pemohon", "tim_kendaraan"].includes(user?.role);
  const filtered = rows.filter((r) =>
    !q || r.requester_name?.toLowerCase().includes(q.toLowerCase()) ||
    r.divisi?.toLowerCase().includes(q.toLowerCase()) ||
    r.branch_name?.toLowerCase().includes(q.toLowerCase()) ||
    r.reason?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
            {user?.role === "pemohon" ? "Pengajuan Saya" : "Semua Pengajuan Kas"}
          </h2>
          <p className="text-sm text-muted-foreground">Daftar pengajuan kas & status pertanggungjawaban.</p>
        </div>
        <div className="flex gap-2">
          {canExport && (
            <ExportDialog testId="requests-export-btn" />
          )}
          {canRequestCash && (
            <Button onClick={() => navigate("/requests/new")} className="bg-brand hover:bg-emerald-800" data-testid="new-request-btn">
              <Plus className="h-4 w-4 mr-2" /> Ajukan Kas
            </Button>
          )}
        </div>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="requests-status-tabs">
              <TabsTrigger value="all" data-testid="tab-all">Semua</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">Menunggu</TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">Disetujui</TabsTrigger>
              <TabsTrigger value="disbursed" data-testid="tab-disbursed">Dicairkan</TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">Selesai</TabsTrigger>
              <TabsTrigger value="rejected" data-testid="tab-rejected">Ditolak</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari pemohon / cabang..."
              className="pl-9" data-testid="requests-search-input" />
          </div>
        </div>
        {canFilterBranch && (
          <div className="mb-4">
            <select value={branch} onChange={(e) => setBranch(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="requests-branch-filter">
              <option value="all">Semua Cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}

        <div className="overflow-x-auto">
          <BulkBar bulk={bulk} onDone={load} label="pengajuan" />
          <Table>
            <TableHeader>
              <TableRow>
                {bulk.enabled && <TableHead className="w-8"><BulkCheckAll bulk={bulk} entries={filtered.map((r) => [r.id, { kind: "cash-requests", id: r.id }])} testId="bulk-request-all" /></TableHead>}
                <TableHead>Tanggal</TableHead>
                <TableHead>Pemohon</TableHead>
                <TableHead>Cabang</TableHead>
                <TableHead>Divisi</TableHead>
                <TableHead className="text-right">Diminta</TableHead>
                <TableHead className="text-right">Disetujui</TableHead>
                <TableHead className="text-right">Sisa Saldo</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">Tidak ada data.</TableCell></TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-secondary/40"
                  onClick={() => navigate(`/requests/${r.id}`)} data-testid={`request-row-${r.id}`}>
                  {bulk.enabled && <TableCell><BulkCheck bulk={bulk} k={r.id} item={{ kind: "cash-requests", id: r.id }} testId={`bulk-request-${r.id}`} /></TableCell>}
                  <TableCell className="text-sm whitespace-nowrap">{formatDate(r.created_at)}</TableCell>
                  <TableCell className="font-medium">{r.requester_name}</TableCell>
                  <TableCell className="text-sm">{r.branch_name || "-"}</TableCell>
                  <TableCell className="text-sm">{r.divisi}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatRupiah(r.amount_requested)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{r.approved_amount != null ? formatRupiah(r.approved_amount) : "-"}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-rose-600">{r.remaining_balance > 0 ? formatRupiah(r.remaining_balance) : "-"}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
